import React, { useCallback, useEffect, useState } from "react";
import { Col, Row, List, Empty, Card, Typography, Checkbox } from "antd";
import JobsImage from "assets/images/headerImage/JobsImage.png";
import axios from "axios";
import HeaderImage from "components/HeaderImage/HeaderImage";
import SearchBar from "components/SearchBar/SearchBar";
import { useLoginContext, isCompanyUser } from "contexts/LoginContext/LoginContext";
import { Job } from "interfaces/jobs";
import { getFilteredJobPostings, getJobPostingStatusMessage } from "./helpers";
import SingleJobPostingBox from "./SingleJobPostingBox/SingleJobPostingBox";
import JobPostingsFilter from "./JobPostingsFilter/JobPostingsFilter";

const { Text } = Typography;

const KDJobPostings = () => {
  const [jobPostings, setJobPostings] = useState<Job[]>();
  const [searchTerm, setSearchTerm] = useState("");
  const [jobTypeChecked, setJobTypeChecked] = useState<string>("Alle stillingstyper");
  const [sectorsChecked, setSectorChecked] = useState<number[]>([]);
  const [locationChecked, setLocationChecked] = useState<string[]>([]);
  const [yearLevelChecked, setYearLevelChecked] = useState<number[]>([]);
  const [publishedThisWeekChecked, setPublishedThisWeekLevelChecked] = useState<boolean>(false);
  const [deadlineChecked, setDeadlineChecked] = useState<string>("");
  const [ownJobs, setOwnJobs] = useState<boolean>(false);

  const { currentUser } = useLoginContext();

  const toggleOwnJobs = () => setOwnJobs(!ownJobs); // Companies only see own events

  useEffect(() => {
    axios.get<Job[]>("/jobs/").then(response => {
      setJobPostings(response.data.filter(job => job.is_kd));
    });
  }, []);

  const filteredJobPostings =
    jobPostings &&
    getFilteredJobPostings(
      jobPostings,
      searchTerm,
      jobTypeChecked,
      sectorsChecked,
      locationChecked,
      yearLevelChecked,
      publishedThisWeekChecked,
      deadlineChecked
    ).map(job => {
      if (!job.is_visible) return null;
      if (ownJobs && job.company !== currentUser?.id) return null;
      /* if (job.is_kd) return null; TODO maybe remove this? */
      return (
        <Col key={job.id} style={{ marginBottom: "1em" }}>
          <SingleJobPostingBox job={job} />
        </Col>
      );
    });

  // Functions to toggle filter and search term states
  const handleSearch = (newSearchTerm: React.SetStateAction<string>) => {
    setSearchTerm(newSearchTerm);
  };

  const toggleCheckedSectors = useCallback(
    (id: number) =>
      setSectorChecked(previousState => {
        if (previousState.includes(id)) return previousState.filter(x => x !== id);
        return [...previousState, id];
      }),
    []
  );

  const toggleCheckedJobType = useCallback((type: string) => {
    setJobTypeChecked(type);
  }, []);

  const toggleCheckedLocations = useCallback(
    (location: string) =>
      setLocationChecked(previousState => {
        if (previousState.includes(location)) return previousState.filter(x => x !== location);
        return [...previousState, location];
      }),
    []
  );

  const toggleCheckedYearLevel = useCallback(
    (year: number) => {
      if (year === yearLevelChecked[0]) {
        setYearLevelChecked([]);
      } else {
        setYearLevelChecked([year]);
      }
    },
    [yearLevelChecked]
  );

  const toggleCheckedDeadline = useCallback(
    (deadline: string) => {
      if (deadline === deadlineChecked) {
        setDeadlineChecked("");
      } else {
        setDeadlineChecked(deadline);
      }
    },
    [deadlineChecked]
  );

  const togglePublishedThisWeek = useCallback(
    () => setPublishedThisWeekLevelChecked(previousState => !previousState),
    []
  );

  // Rendering of components
  return (
    <>
      <HeaderImage imageLink={JobsImage} alt="JobsImage" header="Stillingsannonser" />
      <Row justify="center" style={{ paddingLeft: "5%", paddingRight: "5%", marginTop: "40px", marginBottom: "40px" }}>
        <Col xs={24} sm={24} md={19} lg={19}>
          <SearchBar value={searchTerm} handleSearch={handleSearch} placeholder="Søk..." />
        </Col>
        <Text italic style={{ marginTop: "1em" }}>
          Her kan du se stillingsannonser publisert av bedrifter som deltar på årets KarriereDagene.
        </Text>
      </Row>
      <Row style={{ paddingLeft: "5%", paddingRight: "5%" }}>
        <Col xs={24} sm={24} md={6} lg={6}>
          {isCompanyUser(currentUser) ? (
            <Card bodyStyle={{ padding: "2em" }} style={{ marginBottom: "1em" }}>
              <Col>
                <Checkbox checked={ownJobs} onChange={() => toggleOwnJobs()} value={ownJobs}>
                  <Typography>Vis kun mine stillingsannonser</Typography>
                </Checkbox>
              </Col>
            </Card>
          ) : null}
          <JobPostingsFilter
            sectorsChecked={sectorsChecked}
            yearLevelChecked={yearLevelChecked}
            deadlineChecked={deadlineChecked}
            jobTypeChecked={jobTypeChecked}
            locationsChecked={locationChecked}
            publishedThisWeekChecked={publishedThisWeekChecked}
            toggleCheckedSectors={toggleCheckedSectors}
            toggleCheckedYearLevel={toggleCheckedYearLevel}
            toggleCheckedDeadline={toggleCheckedDeadline}
            toggleCheckedJobType={toggleCheckedJobType}
            toggleCheckedLocations={toggleCheckedLocations}
            togglePublishedThisWeek={togglePublishedThisWeek}
            onFilterReset={() => {
              setSectorChecked([]);
              setDeadlineChecked("");
              setJobTypeChecked("Alle stillingstyper");
              setLocationChecked([]);
              setPublishedThisWeekLevelChecked(false);
              setYearLevelChecked([]);
              handleSearch("");
            }}
          />
        </Col>
        <Col xs={24} sm={24} md={1} lg={1} style={{ minHeight: "2em" }} />
        <Col xs={24} sm={24} md={17} lg={17}>
          <List
            itemLayout="vertical"
            pagination={{
              pageSize: 20,
              responsive: true,
              defaultCurrent: 1,
              defaultPageSize: 50,
              pageSizeOptions: ["20", "50"],
              hideOnSinglePage: true,
            }}
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={
                    jobPostings
                      ? getJobPostingStatusMessage(jobPostings)
                      : "Ingen stillingsannonser er tilgjengelige for øyeblikket"
                  }
                />
              ),
            }}
            dataSource={filteredJobPostings?.filter(job => job)} // In case of null (Not visible or not own)
            renderItem={item => item}
          />
          <br />
        </Col>
      </Row>
    </>
  );
};

export default KDJobPostings;
